import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type {
    AdminCenterClientsQuery,
    AdminCenterClientsResponse,
    AdminCenterTenantActiveStatusPayload,
} from '@routes/sidebar/admin-center/___shared___/api/adminCenter.api.ts';
import {
    deleteTenant,
    getApiAdminCenterClients,
    getApiAdminCenterValidation,
    updateTenantActiveStatus,
} from '@routes/sidebar/admin-center/___shared___/api/adminCenter.api.ts';
import { mapResponseToRows } from '@routes/sidebar/admin-center/___shared___/model/helpers.ts';
import type { AdminCenterValidationResponse } from '@routes/sidebar/admin-center/tenant-client/api/adminCenterValidation.types.ts';
import type { DataType } from '@routes/sidebar/admin-center/tenant-client/ui/tenant-client-groupBy-table/types';
import { getSearchRequestWithNewFilterBool, stringListToFilterRecord } from '@utils/commonSearch';
import { extractError } from '@utils/slice.ts';
import { isEqual } from 'lodash';

export interface AdminCenterClientsError {
    message?: string;
    [k: string]: any;
}

export interface TenantActionsContext {
    clientId: number;
    clientName: string;
    tenantAlias: string;
    tenantName: string;
    tenantType: string;
    isTenantActive: boolean;
}

export interface AdminCenterClientsState {
    clients: {
        isGetAdminCenterClientsPending: boolean;
        error: AdminCenterClientsError | null;
        rows: DataType[];
        response?: AdminCenterClientsResponse;
        query: AdminCenterClientsQuery;
    };
    validation: {
        isGetAdminCenterValidationPending: boolean;
        error: AdminCenterClientsError | null;
        data?: AdminCenterValidationResponse;
    };
    isDeleteTenantModalOpened: boolean;
    isDeactivateTenantModalOpened: boolean;
    isActivateTenantModalOpened: boolean;
    tenantActionsContext: TenantActionsContext | null;
    isUpdateTenantStatusPending: boolean;
    isDeleteTenantPending: boolean;
}

const initialState: AdminCenterClientsState = {
    clients: {
        isGetAdminCenterClientsPending: false,
        error: null,
        rows: [],
        query: {
            pageNumber: 1,
            pageSize: 10,
            textSearch: '',
            sortBy: null,
            sortOrder: null,
            filters: null,
        },
    },
    validation: {
        isGetAdminCenterValidationPending: false,
        error: null,
        data: undefined,
    },
    isDeleteTenantModalOpened: false,
    isDeactivateTenantModalOpened: false,
    isActivateTenantModalOpened: false,
    tenantActionsContext: null,
    isUpdateTenantStatusPending: false,
    isDeleteTenantPending: false,
};

export const getAdminCenterClients = createAsyncThunk<
    AdminCenterClientsResponse,
    AdminCenterClientsQuery,
    { rejectValue: AdminCenterClientsError }
>('adminCenter/getAdminCenterClients', (payload, { rejectWithValue }) =>
    getApiAdminCenterClients(payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getAdminCenterValidation = createAsyncThunk<
    AdminCenterValidationResponse,
    void,
    { rejectValue: AdminCenterClientsError }
>('adminCenter/getAdminCenterValidation', (_: void, { rejectWithValue }) =>
    getApiAdminCenterValidation()
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateTenantStatus = createAsyncThunk<
    void,
    { clientId: number; tenantAlias: string; payload: AdminCenterTenantActiveStatusPayload },
    { rejectValue: AdminCenterClientsError }
>('adminCenter/updateTenantStatus', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    updateTenantActiveStatus(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteClientTenant = createAsyncThunk<
    void,
    { clientId: number; tenantAlias: string },
    { rejectValue: AdminCenterClientsError }
>('adminCenter/deleteClientTenant', async ({ clientId, tenantAlias }, { rejectWithValue }) =>
    deleteTenant(clientId, tenantAlias)
        .then((res) => res)
        .catch((err) => rejectWithValue(err as AdminCenterClientsError)),
);

const adminCenterSlice = createSlice({
    name: 'adminCenter',
    initialState,
    reducers: {
        setTenantType: (state, action: PayloadAction<string[]>) => {
            state.clients.query.filters = stringListToFilterRecord(action.payload, 'TenantType');
            state.clients.query.pageNumber = 1;
        },
        setIsActive: (state, action: PayloadAction<boolean | null>) => {
            state.clients.query.pageNumber = 1;
            if (action.payload === null) return;
            state.clients.query = getSearchRequestWithNewFilterBool(state.clients.query, 'isActive', action.payload);
        },
        updateClientListRequestData: (state, action: PayloadAction<AdminCenterClientsQuery>) => {
            const { textSearch, pageNumber, pageSize, sortOrder, filters, sortBy } = action.payload ?? {};
            const isTextSearchUpdated = state.clients.query.textSearch !== textSearch;
            const isSortByUpdated = sortBy !== state.clients.query.sortBy;
            const isSortOrderUpdated = state.clients.query.sortOrder !== sortOrder;
            const currentFilters = state.clients.query.filters
                ? current(state.clients.query.filters)
                : state.clients.query.filters;
            const isFiltersUpdated = !isEqual(currentFilters, filters);

            state.clients.query.pageNumber = pageNumber;
            state.clients.query.pageSize = pageSize;

            if (isTextSearchUpdated) {
                state.clients.query.textSearch = textSearch;
            }

            if (isSortByUpdated) {
                state.clients.query.sortBy = sortBy;
            }

            if (isSortOrderUpdated) {
                state.clients.query.sortOrder = sortOrder;
            }

            if (isFiltersUpdated) {
                state.clients.query.filters = filters;
            }
            if (isTextSearchUpdated || isSortByUpdated || isSortOrderUpdated || isFiltersUpdated) {
                state.clients.query.pageNumber = 1;
            }
        },
        clearClientsState: (state) => {
            state.clients.rows = [];
            state.clients.response = undefined;
            state.clients.query = {
                pageNumber: 1,
                pageSize: 10,
                textSearch: '',
                sortBy: null,
                sortOrder: null,
                filters: null,
            };
        },
        clearValidationState: (state) => {
            state.validation.data = undefined;
        },
        setIsDeleteTenantModalOpened: (state, action: PayloadAction<boolean>) => {
            state.isDeleteTenantModalOpened = action.payload;
        },
        setIsDeactivateTenantModalOpened: (state, action: PayloadAction<boolean>) => {
            state.isDeactivateTenantModalOpened = action.payload;
        },
        setIsActivateTenantModalOpened: (state, action: PayloadAction<boolean>) => {
            state.isActivateTenantModalOpened = action.payload;
        },
        setTenantActionsContext: (state, action: PayloadAction<TenantActionsContext | null>) => {
            state.tenantActionsContext = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getAdminCenterClients.pending, (state) => {
            state.clients.isGetAdminCenterClientsPending = true;
            state.clients.error = null;
        });
        builder.addCase(getAdminCenterClients.fulfilled, (state, action) => {
            state.clients.isGetAdminCenterClientsPending = false;
            state.clients.response = action.payload;
            state.clients.rows = mapResponseToRows(action.payload);
        });
        builder.addCase(getAdminCenterClients.rejected, (state, action) => {
            state.clients.isGetAdminCenterClientsPending = false;
            state.clients.error = action.payload ?? (action.error.message ? { message: action.error.message } : null);
        });

        builder.addCase(getAdminCenterValidation.pending, (state) => {
            state.validation.isGetAdminCenterValidationPending = true;
            state.validation.error = null;
        });
        builder.addCase(getAdminCenterValidation.fulfilled, (state, action) => {
            state.validation.isGetAdminCenterValidationPending = false;
            state.validation.data = action.payload;
        });
        builder.addCase(getAdminCenterValidation.rejected, (state, action) => {
            state.validation.isGetAdminCenterValidationPending = false;
            state.validation.error = extractError(action);
        });
        builder.addCase(updateTenantStatus.pending, (state) => {
            state.isUpdateTenantStatusPending = true;
            state.clients.error = null;
        });
        builder.addCase(updateTenantStatus.fulfilled, (state) => {
            state.isUpdateTenantStatusPending = false;
        });
        builder.addCase(updateTenantStatus.rejected, (state, action) => {
            state.isUpdateTenantStatusPending = false;
            state.clients.error = extractError(action);
        });
        builder.addCase(deleteClientTenant.pending, (state) => {
            state.isDeleteTenantPending = true;
            state.clients.error = null;
        });
        builder.addCase(deleteClientTenant.fulfilled, (state) => {
            state.isDeleteTenantPending = false;
        });
        builder.addCase(deleteClientTenant.rejected, (state, action) => {
            state.isDeleteTenantPending = false;
            state.clients.error = extractError(action);
        });
        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            state.clients.isGetAdminCenterClientsPending = false;
            const { clientListItemResponse } = action.payload;

            if (!clientListItemResponse) return;

            state.clients.response = clientListItemResponse;
            state.clients.rows = mapResponseToRows(clientListItemResponse);
        });
    },
});

export const {
    setTenantType,
    setIsActive,
    updateClientListRequestData,
    clearClientsState,
    clearValidationState,
    setIsDeactivateTenantModalOpened,
    setIsDeleteTenantModalOpened,
    setIsActivateTenantModalOpened,
    setTenantActionsContext,
} = adminCenterSlice.actions;

export default adminCenterSlice.reducer;
